import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Info } from "lucide-react";
import Seo, { crumbsLd, faqLd } from "@/components/site/Seo";
import { PageHero, FaqList, SectionHead } from "@/components/site/Blocks";
import PricingCards, { PackageCard } from "@/components/site/PricingCards";
import { CtaBand } from "@/components/site/Cta";
import { CRM_PACKAGES, SOCIAL_PACKAGES, PARTNER_EXCLUSIONS, SOCIAL_EXCLUSIONS, SOCIAL_TERMS, VAT_NOTE } from "@/data/pricing";

const FAQS = [
  { q: "I prezzi sono IVA inclusa?", a: "No. Tutti i prezzi sono espressi in euro, IVA esclusa." },
  { q: "Cosa succede se interrompo il servizio di gestione?", a: "Ti consegniamo i contenuti e le esportazioni tecnicamente possibili, ti supportiamo nel trasferimento del dominio (che è intestato a te) e ti comunichiamo per iscritto la data di cessazione dell’hosting. Le migrazioni straordinarie possono essere preventivate." },
  { q: "Le richieste fuori pacchetto quanto costano?", a: "Le tariffe dei servizi aggiuntivi e le richieste fuori pacchetto vengono preventivate separatamente, sempre prima di iniziare il lavoro." },
  { q: "Digital Partner prevede un vincolo?", a: "Sì: contratto iniziale di 12 mesi, per un valore minimo di 4.800 € + IVA. Dopo il primo anno prosegue mese per mese con disdetta a 30 giorni." },
  { q: "Le condizioni contrattuali sono definitive?", a: "Le condizioni di recesso anticipato, inadempimento e trasferimento sono specificate nel contratto, validato legalmente prima della sottoscrizione." },
];

const List = ({ items, icon: I, muted }) => (
  <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
    {items.map((e) => <li key={e} className={`flex gap-2.5 text-sm ${muted ? "text-slate-600" : ""}`}><I className={`mt-0.5 h-4 w-4 shrink-0 ${muted ? "text-slate-600" : "text-blue-600"}`} />{e}</li>)}
  </ul>
);

export default function Pricing() {
  const crumbs = [{ label: "Home", to: "/" }, { label: "Prezzi", to: "/prezzi" }];
  return (
    <>
      <Seo path="/prezzi" title="Prezzi siti web, CRM e gestione social | STUDIO WEB NS"
        description="Listino trasparente: siti da 490 € + 39 €/mese, Business 890 € + 59 €/mese, Premium 1.490 € + 99 €/mese, Digital Partner 400 €/mese. CRM e social. IVA esclusa."
        jsonLd={[crumbsLd(crumbs), faqLd(FAQS)]} />
      <PageHero crumbs={crumbs} eyebrow="Prezzi" title="Soluzioni professionali. Prezzi chiari. Nessuna sorpresa." intro={`${VAT_NOTE} Le richieste fuori pacchetto vengono preventivate separatamente.`} />

      <section className="py-14 md:py-20" id="siti">
        <div className="wrap">
          <SectionHead eyebrow="Siti web" title="Pacchetti sito e gestione" />
          <div className="mt-12"><PricingCards /></div>
          <div className="mt-10 rounded-3xl border border-slate-200 p-8">
            <h3 className="font-display text-lg font-bold">Digital Partner: cosa non è incluso</h3>
            <p className="mt-2 text-sm text-slate-600">Gestione continuativa con costi chiari e interventi ordinari inclusi. Restano esclusi, e preventivabili a parte:</p>
            <List items={PARTNER_EXCLUSIONS} icon={X} muted />
          </div>
        </div>
      </section>

      <section className="bg-soft py-14 md:py-20" id="crm">
        <div className="wrap">
          <SectionHead eyebrow="CRM per la tua azienda" title="Organizza clienti e trattative" />
          <div className="mt-12 grid gap-5 md:grid-cols-2">{CRM_PACKAGES.map((p) => <PackageCard key={p.id} p={p} />)}</div>
        </div>
      </section>

      <section className="py-14 md:py-20" id="social">
        <div className="wrap">
          <SectionHead eyebrow="Gestione social" title="Instagram e Facebook, ogni mese" intro={SOCIAL_TERMS} />
          <div className="mt-12 grid gap-5 md:grid-cols-2">{SOCIAL_PACKAGES.map((p) => <PackageCard key={p.id} p={p} />)}</div>
          <div className="mt-10 rounded-3xl border border-slate-200 p-8">
            <h3 className="font-display text-lg font-bold">Esclusi dai pacchetti social</h3>
            <List items={SOCIAL_EXCLUSIONS} icon={X} muted />
          </div>
        </div>
      </section>

      <section className="bg-soft py-14 md:py-20">
        <div className="wrap grid gap-6 md:grid-cols-3">
          {[["E-commerce", "Su preventivo, indicativamente da 2.500 € + IVA per progetti semplici.", "/servizi/e-commerce"], ["Se interrompi la gestione", "Consegna contenuti, supporto al trasferimento del dominio, data di cessazione hosting comunicata per iscritto.", "/proprieta-e-disdetta"], ["Condizioni", "Bozza delle condizioni generali, da validare legalmente prima della firma.", "/condizioni-generali"]].map(([t, d, to]) => (
            <Link key={t} to={to} className="card group p-7 transition-colors hover:border-slate-900">
              <Info className="h-5 w-5 text-blue-600" />
              <h3 className="mt-4 font-display text-lg font-bold">{t}</h3>
              <p className="mt-2 text-sm text-slate-600">{d}</p>
              <span className="link-arrow mt-4">Approfondisci <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <SectionHead eyebrow="FAQ" title="Domande sui prezzi" />
            <ul className="mt-8 space-y-2 text-sm text-slate-600">
              {["Prezzi pubblici", "IVA esclusa sempre indicata", "Nessun lavoro extra senza preventivo"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-blue-600" />{t}</li>)}
            </ul>
          </div>
          <FaqList faqs={FAQS} id="pricing-faq" />
        </div>
      </section>
      <CtaBand />
    </>
  );
}
